import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { organizationsListMyOrgs, organizationsSetActive } from "../api-refs";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/spinner";

export const Route = createFileRoute("/select-organization")({
  component: SelectOrganizationPage,
});

function SelectOrganizationPage() {
  const navigate = useNavigate();
  // Gate the query on Convex auth readiness. The vortex-auth bearer token is
  // attached to the Convex client asynchronously after sign-up; firing
  // `listMyOrgs` before the token lands makes `requireUser` throw
  // `ConvexError("Authentication required")`, which the router error boundary
  // latches into a permanent "Server Error" page that never recovers when auth
  // resolves. Skipping until authenticated mirrors `_authenticated.tsx`.
  const { isAuthenticated } = useConvexAuth();
  const orgs = useQuery(organizationsListMyOrgs, isAuthenticated ? {} : "skip");
  const setActive = useMutation(organizationsSetActive);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onPick(vortexOrgId: string) {
    setError(null);
    setPendingId(vortexOrgId);
    try {
      await setActive({ vortexOrgId });
      await navigate({ to: "/" });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to switch organization"
      );
      setPendingId(null);
    }
  }

  if (orgs === undefined) {
    return (
      <div className="bg-muted flex min-h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="bg-muted flex min-h-dvh items-center justify-center p-4">
      <div className="bg-background w-full max-w-md rounded-md border p-6 shadow-sm">
        <h1 className="text-xl font-semibold">Select organization</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Choose which workspace you want to open.
        </p>

        {orgs.length === 0 ? (
          <div className="mt-6 space-y-4">
            <p className="text-sm">
              You're not a member of any organization yet.
            </p>
            <Button asChild className="w-full">
              <Link to="/create-organization">Create an organization</Link>
            </Button>
          </div>
        ) : (
          <>
            <ul className="mt-6 space-y-2">
              {orgs.map((o) => {
                const orgId = o.vortexAuthOrganizationId ?? o.organizationId;
                return (
                  <li key={o._id}>
                    <button
                      className="hover:bg-accent flex w-full items-center justify-between rounded-md border px-4 py-3 text-left disabled:opacity-60"
                      disabled={pendingId !== null}
                      onClick={() => onPick(orgId)}
                      type="button"
                    >
                      <span className="text-sm font-medium">
                        {o.businessName || "Unnamed organization"}
                      </span>
                      {pendingId === orgId ? (
                        <Spinner className="size-4" />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-6 border-t pt-4">
              <Button asChild className="w-full" variant="outline">
                <Link to="/create-organization">Create new organization</Link>
              </Button>
            </div>
          </>
        )}

        {error ? (
          <p className="text-destructive mt-4 text-sm" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
